
/**
 * Enterprise Service Logger
 * 
 * Structured logging system with correlation IDs, performance metrics,
 * and production-ready debugging capabilities for service operations.
 */

import { PerformanceMetrics, OperationContext } from '@/types/services';
import { ServiceError } from '@/types/errors';

/**
 * Log level enumeration
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4
}

/**
 * Structured log entry interface
 */
export interface LogEntry {
  readonly timestamp: string;
  readonly level: LogLevel;
  readonly message: string;
  readonly correlationId: string;
  readonly operationId?: string;
  readonly userId?: string;
  readonly organizationId?: string;
  readonly service: string;
  readonly method: string;
  readonly duration?: number;
  readonly error?: ServiceError;
  readonly metrics?: PerformanceMetrics;
  readonly context?: Record<string, unknown>;
  readonly tags?: string[];
  readonly environment: string;
}

/**
 * Logger configuration
 */
export interface LoggerConfig {
  readonly level: LogLevel;
  readonly enableConsole: boolean;
  readonly enableRemote: boolean;
  readonly remoteEndpoint?: string;
  readonly enableMetrics: boolean;
  readonly enableStackTrace: boolean;
  readonly maxContextSize: number; // bytes
  readonly correlationIdHeader: string;
  readonly environment: string;
}

/**
 * Performance timer for measuring operation duration
 */
class PerformanceTimer {
  private readonly startTime: number;
  private readonly operationId: string;
  private databaseQueries: number = 0;
  private cacheHits: number = 0;
  private cacheMisses: number = 0;
  private retryAttempts: number = 0;

  constructor(operationId: string) {
    this.startTime = performance.now();
    this.operationId = operationId;
  }

  /**
   * Record a database query
   */
  recordDatabaseQuery(): void {
    this.databaseQueries++;
  }

  /**
   * Record a cache hit
   */
  recordCacheHit(): void {
    this.cacheHits++;
  }

  /**
   * Record a cache miss
   */
  recordCacheMiss(): void {
    this.cacheMisses++;
  }

  /**
   * Record a retry attempt
   */
  recordRetry(): void {
    this.retryAttempts++;
  }

  /**
   * Get final performance metrics
   */
  getMetrics(): PerformanceMetrics {
    const endTime = performance.now();
    return {
      operationId: this.operationId,
      startTime: this.startTime,
      endTime,
      duration: endTime - this.startTime,
      databaseQueries: this.databaseQueries > 0 ? this.databaseQueries : undefined,
      cacheHits: this.cacheHits > 0 ? this.cacheHits : undefined,
      cacheMisses: this.cacheMisses > 0 ? this.cacheMisses : undefined,
      retryAttempts: this.retryAttempts > 0 ? this.retryAttempts : undefined
    };
  }
}

/**
 * Enterprise-grade logger for service operations
 */
export class ServiceLogger {
  private readonly config: LoggerConfig;
  private readonly activeTimers = new Map<string, PerformanceTimer>();

  constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      level: LogLevel.INFO,
      enableConsole: true,
      enableRemote: false,
      enableMetrics: true,
      enableStackTrace: false,
      maxContextSize: 10240, // 10KB
      correlationIdHeader: 'X-Correlation-ID',
      environment: process.env.NODE_ENV || 'development',
      ...config
    };
  }

  /**
   * Create a correlation ID for request tracking
   */
  static createCorrelationId(): string {
    return `svc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create an operation ID for performance tracking
   */
  static createOperationId(service: string, method: string): string {
    return `${service}_${method}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
  }

  /**
   * Start performance tracking for an operation
   */
  startOperation(operationId: string): PerformanceTimer {
    const timer = new PerformanceTimer(operationId);
    this.activeTimers.set(operationId, timer);
    return timer;
  }

  /**
   * End performance tracking and get metrics
   */
  endOperation(operationId: string): PerformanceMetrics | undefined {
    const timer = this.activeTimers.get(operationId);
    if (timer) {
      this.activeTimers.delete(operationId);
      return timer.getMetrics();
    }
    return undefined;
  }

  /**
   * Log debug information
   */
  debug(
    message: string,
    correlationId: string,
    service: string,
    method: string,
    context?: Record<string, unknown>
  ): void {
    this.log(LogLevel.DEBUG, message, correlationId, service, method, context);
  }

  /**
   * Log informational message
   */
  info(
    message: string,
    correlationId: string,
    service: string,
    method: string,
    context?: Record<string, unknown>
  ): void {
    this.log(LogLevel.INFO, message, correlationId, service, method, context);
  }

  /**
   * Log warning message
   */
  warn(
    message: string,
    correlationId: string,
    service: string,
    method: string,
    context?: Record<string, unknown>
  ): void {
    this.log(LogLevel.WARN, message, correlationId, service, method, context);
  }

  /**
   * Log error with full context
   */
  error(
    message: string,
    error: ServiceError,
    correlationId: string,
    service: string,
    method: string,
    context?: Record<string, unknown>
  ): void {
    this.log(LogLevel.ERROR, message, correlationId, service, method, context, error);
  }

  /**
   * Log fatal error
   */
  fatal(
    message: string,
    error: ServiceError,
    correlationId: string,
    service: string,
    method: string,
    context?: Record<string, unknown>
  ): void {
    this.log(LogLevel.FATAL, message, correlationId, service, method, context, error);
  }

  /**
   * Log operation completion with metrics
   */
  logOperation(
    message: string,
    correlationId: string,
    service: string,
    method: string,
    metrics: PerformanceMetrics,
    context?: Record<string, unknown>
  ): void {
    const _entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.INFO,
      message,
      correlationId,
      operationId: metrics.operationId,
      service,
      method,
      duration: metrics.duration,
      metrics,
      context: this.sanitizeContext(context),
      environment: this.config.environment
    };

    this.writeLog(_entry);

    // Log performance warnings if operation is slow
    if (metrics.duration > 5000) { // 5 seconds
      this.warn(
        `Slow operation detected: ${method} took ${metrics.duration.toFixed(2)}ms`,
        correlationId,
        service,
        method,
        { metrics }
      );
    }

    // Log potential N+1 query problems
    if (metrics.databaseQueries && metrics.databaseQueries > 10) {
      this.warn(
        `High database query count: ${metrics.databaseQueries} queries in ${method}`,
        correlationId,
        service,
        method,
        { metrics }
      );
    }
  }

  /**
   * Log cache performance
   */
  logCacheMetrics(
    correlationId: string,
    service: string,
    method: string,
    cacheKey: string,
    hit: boolean,
    ttl?: number
  ): void {
    const message = hit ? 'Cache hit' : 'Cache miss';
    this.debug(message, correlationId, service, method, {
      cacheKey,
      hit,
      ttl
    });
  }

  /**
   * Log retry attempt
   */
  logRetry(
    correlationId: string,
    service: string,
    method: string,
    attempt: number,
    maxAttempts: number,
    error: ServiceError,
    nextRetryIn?: number
  ): void {
    this.warn(
      `Retry attempt ${attempt}/${maxAttempts} for ${method}`,
      correlationId,
      service,
      method,
      {
        attempt,
        maxAttempts,
        nextRetryIn,
        errorCode: error.code,
        retryable: error.retryable
      }
    );
  }

  /**
   * Log audit event for compliance
   */
  audit(
    action: string,
    auditData: Record<string, unknown>
  ): void {
    const correlationId = ServiceLogger.createCorrelationId();
    const _entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.INFO,
      message: `AUDIT: ${action}`,
      correlationId,
      service: 'AuditService',
      method: action,
      context: {
        ...auditData,
        _auditEvent: true,
        _auditTimestamp: new Date().toISOString()
      },
      environment: this.config.environment,
      tags: ['audit', action]
    };

    // Always write audit logs regardless of log level
    this.writeLog(_entry);

    // In production, audit logs should also be written to:
    // - Immutable audit log storage
    // - Compliance reporting system
    // - Security information and event management (SIEM) system
  }

  /**
   * Core logging method
   */
  private log(
    level: LogLevel,
    message: string,
    correlationId: string,
    service: string,
    method: string,
    context?: Record<string, unknown>,
    error?: ServiceError
  ): void {
    if (level < this.config.level) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      correlationId,
      service,
      method,
      error,
      context: this.sanitizeContext(context),
      environment: this.config.environment
    };

    this.writeLog(entry);
  }

  /**
   * Write log entry to configured outputs
   */
  private writeLog(_entry: LogEntry): void {
    if (this.config.enableConsole) {
      this.writeToConsole(_entry);
    }

    if (this.config.enableRemote && this.config.remoteEndpoint) {
      this.writeToRemote(_entry);
    }

    // In production, you might also write to:
    // - Structured logging service (e.g., LogRocket, DataDog)
    // - Error tracking service (e.g., S_entry)
    // - Analytics platform for metrics
  }

  /**
   * Write to browser console with appropriate styling
   */
  private writeToConsole(_entry: LogEntry): void {
    const prefix = `[${_entry.service}:${_entry.method}] ${_entry.correlationId}`;
    const message = `${prefix} ${_entry.message}`;

    switch (_entry.level) {
      case LogLevel.DEBUG:
        console.log(`🔍 ${message}`, _entry.context || '');
        break;
      case LogLevel.INFO:
        console.info(`ℹ️ ${message}`, _entry.context || '');
        break;
      case LogLevel.WARN:
        console.warn(`⚠️ ${message}`, _entry.context || '');
        break;
      case LogLevel.ERROR:
        console.error(`❌ ${message}`, _entry.error || '', _entry.context || '');
        break;
      case LogLevel.FATAL:
        console.error(`💀 ${message}`, _entry.error || '', _entry.context || '');
        break;
    }

    // Log performance metrics if available
    if (_entry.metrics && this.config.enableMetrics) {
      console.info(`📊 Performance [${_entry.correlationId}]:`, {
        duration: `${_entry.metrics.duration.toFixed(2)}ms`,
        queries: _entry.metrics.databaseQueries,
        cacheHits: _entry.metrics.cacheHits,
        cacheMisses: _entry.metrics.cacheMisses,
        retries: _entry.metrics.retryAttempts
      });
    }
  }

  /**
   * Write to remote logging service
   */
  private writeToRemote(_entry: LogEntry): void {
    // In production, implement actual remote logging
    // This might use fetch() to send logs to your logging service
    if (this.config.environment === 'development') {
       // Skip remote logging in development
    }

    // Example implementation:
    /*
    fetch(this.config.remoteEndpoint!, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [this.config.correlationIdHeader]: entry.correlationId
      },
      body: JSON.stringify(_entry)
    }).catch(error => {
      logger.error('Failed to send log to remote service:', error);
    });
    */
  }

  /**
   * Sanitize context data to prevent sensitive information leakage
   */
  private sanitizeContext(context?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!context) {
return undefined;
}

    const sensitiveKeys = ['password', 'token', 'key', 'secret', 'authorization', 'ssn', 'social'];
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(context)) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'string' && value.length > this.config.maxContextSize) {
        sanitized[key] = `[TRUNCATED: ${value.length} chars]`;
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}

/**
 * Global logger instance with default configuration
 */
export const logger = new ServiceLogger({
  level: process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO,
  enableConsole: true,
  enableRemote: process.env.NODE_ENV === 'production',
  enableMetrics: true,
  enableStackTrace: process.env.NODE_ENV === 'development'
});

/**
 * Helper function to create operation context
 */
export function createOperationContext(
  service: string,
  method: string,
  options?: Partial<OperationContext>
): OperationContext {
  return {
    operationId: ServiceLogger.createOperationId(service, method),
    correlationId: options?.correlationId || ServiceLogger.createCorrelationId(),
    userId: options?.userId,
    organizationId: options?.organizationId,
    userAgent: options?.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
    sessionId: options?.sessionId,
    requestId: options?.requestId,
    startTime: performance.now(),
    metadata: options?.metadata,
    ...options
  };
}